import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Observable, Subject, debounceTime } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { TarifaService } from '../../../services/tarifa/tarifa.service';
import { TipoProductoService } from '../../../services/tipo-producto/tipo-producto.service';
import { ProgramaService } from '../../../services/programa/programa.service';
import { TipoMaterialService } from '../../../services/tipo-material/tipo-material.service';
import { FormatoDigitalService } from '../../../services/formato-digital/formato-digital.service';
import { UbicacionDigitalService } from '../../../services/ubicacion-digital/ubicacion-digital.service';
import { Tarifa } from '../../../domain/tarifa.domain';
import { TarifaDetalle } from '../../../domain/tarifa-detalle.domain';
import { TipoProducto } from '../../../domain/tipo-producto.domain';
import { FormatoDigital } from '../../../domain/formato-digital.domain';
import { UbicacionDigital } from '../../../domain/ubicacion-digital.domain';
import { environment } from '../../../../environments/environment';
import { analyzeTarifaDetalles, TarifaDetalleAnalyzerIssue, TarifaDetalleAnalyzerResult } from '../../../services/pricing/tarifa-detalle-analyzer';
import { TarifaIssuesSnapshot } from '../../../domain/tarifa-issues.domain';
import { TarifaDetalleApiService } from '../../../services/tarifa-detalle/tarifa-detalle-api.service';
import { TarifaDetalleWritePayload } from '../../../domain/tarifa-detalle-write.domain';

type TarifaTipoTab = {
  codigo: string;
  label: string;
  tipoProducto?: TipoProducto | null;
  disabled?: boolean;
};

type GroupedIssue = {
  key: string;
  type: 'ERROR' | 'WARN';
  code: string;
  rowIndex: number;
  detalleId?: number | null;
  fields: string[];
  message: string;
  relatedRowIndexes: number[];
  count: number;
};

@Component({
  selector: 'app-tarifa-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    ButtonModule,
    DatePickerModule,
    InputNumberModule,
    AutoCompleteModule,
    SelectModule,
    DialogModule,
    TooltipModule
  ],
  templateUrl: './tarifa-edit.component.html',
  styleUrls: ['./tarifa-edit.component.scss']
})
export class TarifaEditComponent implements OnInit, OnDestroy {
  tarifaForm!: FormGroup;
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  tarifaId: string | null = null;
  tarifa = signal<Tarifa | null>(null);
  serverIssues = signal<TarifaIssuesSnapshot | null>(null);
  issuesLoading = signal<boolean>(false);
  selectedDetalleIndex = signal<number | null>(null);
  showAddDetalleDialog = signal<boolean>(false);
  detallesInvalid = signal<boolean>(false);
  tipoProductoOptions = signal<TipoProducto[]>([]);
  tipoTabs = signal<TarifaTipoTab[]>([]);
  activeTipoCodigo = signal<string | null>(null);
  detalleAnalysis = signal<Record<string, TarifaDetalleAnalyzerResult>>({});
  inconsistenciesOnlyByTipo = signal<Record<string, boolean>>({});
  expandedIssueGroups = signal<Record<string, boolean>>({});
  highlightedRows = signal<Set<number>>(new Set());
  resolveDialogVisible = signal<boolean>(false);
  resolveKey = signal<string>('');
  resolveDetalleIds = signal<number[]>([]);
  resolveAId = signal<number | null>(null);
  resolveBId = signal<number | null>(null);
  showWarningsDialog = signal<boolean>(false);
  programaSuggestions = signal<any[]>([]);
  tipoMaterialSuggestions = signal<any[]>([]);
  formatoDigitalSuggestions = signal<FormatoDigital[]>([]);
  ubicacionDigitalSuggestions = signal<UbicacionDigital[]>([]);
  detalleDraftForm!: FormGroup;

  diaTipoOptions = [
    { label: 'Lun-Vie', value: 'LUN_VIE' },
    { label: 'Sábado', value: 'SABADO' },
    { label: 'Domingo', value: 'DOMINGO' },
    { label: 'Feriado', value: 'FERIADO' }
  ];

  private destroy$ = new Subject<void>();
  private hydratedEntityCache = new Map<string, any>();
  private detalleRawTipoProducto = new WeakMap<FormGroup, any>();
  private readonly tipoTabConfig: Array<{ codigo: string; label: string }> = [
    { codigo: 'AIRE_SPOT', label: 'Aire Spot' },
    { codigo: 'WEB_BANNER', label: 'Banner Web' },
    { codigo: 'REDES_PIEZA', label: 'Redes' },
    { codigo: 'PUBLINOTA', label: 'Publinotas' },
    { codigo: 'COBERTURA_EVENTO', label: 'Cobertura' },
    { codigo: 'PNT', label: 'PNT' }
  ];
  private readonly detalleFieldLabels: Record<string, string> = {
    tipoProductoId: 'Tipo producto',
    unidadTarifacion: 'Unidad',
    precioUnitario: 'Precio',
    diaTipo: 'Día',
    horaDesde: 'Hora desde',
    horaHasta: 'Hora hasta',
    programa: 'Programa',
    tipoMaterial: 'Tipo material',
    formatoDigital: 'Formato',
    ubicacionDigital: 'Ubicación',
    plataformaRedes: 'Plataforma',
    duplicate: 'Duplicado',
    overlap: 'Solapamiento'
  };

  private tarifaService = inject(TarifaService);
  private tarifaDetalleApiService = inject(TarifaDetalleApiService);
  private tipoProductoService = inject(TipoProductoService);
  private programaService = inject(ProgramaService);
  private tipoMaterialService = inject(TipoMaterialService);
  private formatoDigitalService = inject(FormatoDigitalService);
  private ubicacionDigitalService = inject(UbicacionDigitalService);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupRouteParamListener();
    this.loadTipoProductos();
    this.setupDetalleAnalysis();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupRouteParamListener(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newId = params.get('id');
        if (newId !== this.tarifaId) {
          this.loadTarifa();
        }
      });
  }

  initForm(): void {
    this.tarifaForm = this.fb.group({
      codigo: ['', [Validators.required, Validators.maxLength(255)]],
      nombre: ['', [Validators.required, Validators.maxLength(255)]],
      descripcion: ['', Validators.maxLength(255)],
      vigenciaDesde: [null, Validators.required],
      vigenciaHasta: [null, Validators.required],
      active: [true],
      tarifaDetalles: this.fb.array([])
    });
    this.detalleDraftForm = this.createDetalleDraftForm();
    if (environment.pricingDebug) {
      this.tarifaForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(status => {
        if (status === 'INVALID') {
          console.debug('[TARIFA_DETALLE_EDIT] tarifaForm status invalid', this.getInvalidControls(this.tarifaForm));
        }
      });
    }
  }

  loadTarifa(): void {
    const newTarifaId = this.route.snapshot.paramMap.get('id');

    if (newTarifaId !== this.tarifaId) {
      this.tarifaForm.reset({ active: true });
      this.isEditMode.set(false);
      this.tarifa.set(null);
    }

    this.tarifaId = newTarifaId;

    if (this.tarifaId && this.tarifaId !== 'nuevo') {
      this.isEditMode.set(true);
      this.loading.set(true);

      this.tarifaService.getById(this.tarifaId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (tarifa: Tarifa) => {
            this.loading.set(false);
            this.tarifa.set(tarifa);
            this.procesarDatosTarifa(tarifa);
            this.refreshIssues();
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error al cargar tarifa:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al cargar los datos de la tarifa',
              life: 3000
            });
          }
        });
    } else {
      this.isEditMode.set(false);
    }
  }

  private procesarDatosTarifa(tarifa: Tarifa): void {
    this.tarifaForm.patchValue({
      codigo: tarifa.codigo || '',
      nombre: tarifa.nombre || '',
      descripcion: tarifa.descripcion || '',
      vigenciaDesde: tarifa.vigenciaDesde ? new Date(tarifa.vigenciaDesde) : null,
      vigenciaHasta: tarifa.vigenciaHasta ? new Date(tarifa.vigenciaHasta) : null,
      active: tarifa.active ?? true
    });

    this.tarifaDetallesFormArray.clear();
    (tarifa.tarifaDetalles || []).forEach(detalle => {
      this.tarifaDetallesFormArray.push(this.createDetalleGroup(detalle));
    });
    this.syncTabs();
    this.refreshDetalleAnalysis();
    const active = this.activeTipoCodigo() ?? this.tipoTabs()[0]?.codigo ?? null;
    if (active) {
      this.selectTipoTab(active);
    } else {
      this.selectedDetalleIndex.set(null);
    }
    if (environment.pricingDebug) {
      console.debug('[TARIFA_DETALLE_EDIT] tarifaForm after load', {
        invalidControls: this.getInvalidControls(this.tarifaForm),
        detallesMissing: this.tarifaDetallesFormArray.controls.map((control, index) => ({
          index,
          id: (control as FormGroup).get('id')?.value ?? null,
          missing: this.getDetalleMissingFields(control as FormGroup)
        }))
      });
    }
  }

  get tarifaDetallesFormArray(): FormArray {
    return this.tarifaForm.get('tarifaDetalles') as FormArray;
  }

  createDetalleGroup(detalle?: Partial<TarifaDetalle>): FormGroup {
    const group = this.fb.group({
      id: [detalle?.id || null],
      tipoProductoId: [this.extractId(detalle?.tipoProducto) ?? null, [Validators.required]],
      unidadTarifacion: [detalle?.unidadTarifacion || null, [Validators.required]],
      precioUnitario: [detalle?.precioUnitario ?? null, [Validators.required, Validators.min(0)]],
      prioridad: [detalle?.prioridad ?? 0],
      diaTipo: [detalle?.diaTipo ?? null],
      horaDesde: [this.parseTimeToDate(detalle?.horaDesde)],
      horaHasta: [this.parseTimeToDate(detalle?.horaHasta)],
      programa: [this.normalizeEntity(detalle?.programa) ?? null],
      tipoMaterial: [this.normalizeEntity(detalle?.tipoMaterial) ?? null],
      formatoDigital: [this.normalizeEntity(detalle?.formatoDigital) ?? null],
      ubicacionDigital: [this.normalizeEntity(detalle?.ubicacionDigital) ?? null],
      plataformaRedes: [detalle?.plataformaRedes ?? null]
    });

    if (detalle?.tipoProducto) {
      this.detalleRawTipoProducto.set(group, detalle.tipoProducto);
    }
    this.seedDetalleRelation(detalle?.programa, this.programaSuggestions);
    this.seedDetalleRelation(detalle?.tipoMaterial, this.tipoMaterialSuggestions);
    this.seedDetalleRelation(detalle?.formatoDigital, this.formatoDigitalSuggestions);
    this.seedDetalleRelation(detalle?.ubicacionDigital, this.ubicacionDigitalSuggestions);

    this.applyDetalleValidators(group);
    group.get('tipoProductoId')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const tipoProducto = this.getTipoProductoById(value, group);
      if (tipoProducto?.unidadTarifacion) {
        group.patchValue({ unidadTarifacion: tipoProducto.unidadTarifacion }, { emitEvent: false });
      }
      this.applyDetalleValidators(group);
    });
    group.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.detallesInvalid()) {
        this.detallesInvalid.set(false);
      }
    });

    return group;
  }

  removeDetalle(index: number): void {
    this.tarifaDetallesFormArray.removeAt(index);
    const selected = this.selectedDetalleIndex();
    if (selected === index) {
      this.selectedDetalleIndex.set(null);
    } else if (selected !== null && selected > index) {
      this.selectedDetalleIndex.set(selected - 1);
    }
    this.syncTabs();
    this.refreshDetalleAnalysis();
  }

  deleteDetalle(index: number): void {
    const group = this.tarifaDetallesFormArray.at(index) as FormGroup | undefined;
    if (!group) return;

    const id = group.get('id')?.value ?? null;
    if (!id) {
      this.removeDetalle(index);
      return;
    }

    const ok = window.confirm('¿Eliminar este detalle? Esta acción no se puede deshacer.');
    if (!ok) return;

    console.debug('[TARIFA_UI] deleteDetalle start id=' + id);
    this.loading.set(true);

    this.tarifaDetalleApiService.deleteDetalle(Number(id))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          console.debug('[TARIFA_UI] deleteDetalle ok id=' + id);
          this.loading.set(false);
          this.removeDetalle(index);
          this.refreshIssues();
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Detalle eliminado',
            life: 2500
          });
        },
        error: (error) => {
          this.loading.set(false);
          console.error('[TARIFA_UI] deleteDetalle error', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al eliminar el detalle',
            life: 4000
          });
        }
      });
  }

  selectDetalle(index: number): void {
    if (index < 0 || index >= this.tarifaDetallesFormArray.length) {
      return;
    }
    this.selectedDetalleIndex.set(index);
    const group = this.tarifaDetallesFormArray.at(index) as FormGroup;
    if (environment.pricingDebug) {
      console.debug('[TARIFA_DETALLE_EDIT] open', {
        detalleId: group.get('id')?.value ?? null,
        tipoProductoId: group.get('tipoProductoId')?.value ?? null,
        rawTipoProducto: this.detalleRawTipoProducto.get(group) ?? null
      });
    }
    this.hydrateDetalleIris(group);
  }

  duplicateDetalle(index: number): void {
    const source = this.tarifaDetallesFormArray.at(index) as FormGroup;
    const value = source.value;
    const duplicated = this.createDetalleGroup({
      ...value,
      id: null,
      prioridad: (value.prioridad ?? 0) + 1
    } as TarifaDetalle);
    this.tarifaDetallesFormArray.push(duplicated);
    this.selectDetalle(this.tarifaDetallesFormArray.length - 1);
    this.syncTabs();
    this.refreshDetalleAnalysis();
  }

  closeDetallePanel(): void {
    this.selectedDetalleIndex.set(null);
  }

  saveSelectedDetalle(): void {
    const group = this.getSelectedDetalleForm();
    if (!group) return;
    if (!this.tarifaId) return;
    if (!this.isEditMode()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Primero guardá la tarifa',
        detail: 'Guardá la cabecera antes de guardar detalles.',
        life: 3500
      });
      return;
    }

    if (group.invalid) {
      group.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Detalle inválido',
        detail: 'Revisá los campos requeridos del detalle.',
        life: 3500
      });
      return;
    }

    const id = group.get('id')?.value ?? null;
    const payload = this.buildTarifaDetalleWritePayload(group.value, this.tarifaId, !id);
    console.debug('[TARIFA_UI] saveDetalle start id=' + (id ?? 'new'));

    this.loading.set(true);
    const request$ = id
      ? this.tarifaDetalleApiService.updateDetalle(Number(id), payload)
      : this.tarifaDetalleApiService.createDetalle(payload);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (saved: any) => {
          const savedId = saved?.id ?? id;
          if (!id && savedId) {
            group.patchValue({ id: savedId }, { emitEvent: false });
          }
          group.markAsPristine();
          console.debug('[TARIFA_UI] saveDetalle ok id=' + savedId);
          this.loading.set(false);
          this.refreshIssues();
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Detalle guardado',
            life: 2500
          });
        },
        error: (error) => {
          this.loading.set(false);
          console.error('[TARIFA_UI] saveDetalle error', error);
          let errorMessage = 'Error al guardar el detalle';
          if (error?.error?.violations?.length) {
            errorMessage = error.error.violations.map((v: any) => v.message).join(', ');
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000
          });
        }
      });
  }

  handleDetallePanelKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDetallePanel();
    }
    if (event.key === 'Enter' && !(event.target instanceof HTMLTextAreaElement)) {
      event.preventDefault();
      this.closeDetallePanel();
    }
  }

  handleListKeydown(event: KeyboardEvent): void {
    const rows = this.getDetalleRowsForTipoFiltered(this.activeTipoCodigo());
    if (!rows.length) {
      return;
    }
    const currentIndex = this.selectedDetalleIndex();
    const currentPos = rows.findIndex(row => row.index === currentIndex);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = currentPos === -1 ? rows[0] : rows[Math.min(currentPos + 1, rows.length - 1)];
      this.selectDetalle(next.index);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = currentPos === -1 ? rows[rows.length - 1] : rows[Math.max(currentPos - 1, 0)];
      this.selectDetalle(prev.index);
    }
  }

  openAddDetalleDialog(): void {
    this.detalleDraftForm.reset({
      tipoProductoId: null,
      precioUnitario: null,
      prioridad: 0,
      diaTipo: null,
      horaDesde: null,
      horaHasta: null,
      formatoDigital: null,
      ubicacionDigital: null,
      plataformaRedes: null
    });
    this.showAddDetalleDialog.set(true);
  }

  confirmAddDetalle(): void {
    if (this.detalleDraftForm.invalid) {
      this.detalleDraftForm.markAllAsTouched();
      return;
    }
    const value = this.detalleDraftForm.value;
    const tipoProducto = this.getTipoProductoById(value.tipoProductoId) ?? undefined;
    const group = this.createDetalleGroup({
      tipoProducto: tipoProducto ?? undefined,
      unidadTarifacion: tipoProducto?.unidadTarifacion ?? undefined,
      precioUnitario: value.precioUnitario,
      prioridad: value.prioridad ?? 0,
      diaTipo: value.diaTipo,
      horaDesde: value.horaDesde,
      horaHasta: value.horaHasta,
      formatoDigital: value.formatoDigital ?? undefined,
      ubicacionDigital: value.ubicacionDigital ?? undefined,
      plataformaRedes: value.plataformaRedes
    });
    this.tarifaDetallesFormArray.push(group);
    this.selectDetalle(this.tarifaDetallesFormArray.length - 1);
    this.showAddDetalleDialog.set(false);
    this.syncTabs();
    this.refreshDetalleAnalysis();
  }

  cancelAddDetalle(): void {
    this.showAddDetalleDialog.set(false);
  }

  private applyDetalleValidators(group: FormGroup): void {
    const tipoProducto = this.getTipoProductoById(group.get('tipoProductoId')?.value, group);
    const diaTipo = group.get('diaTipo');
    const horaDesde = group.get('horaDesde');
    const horaHasta = group.get('horaHasta');
    const formatoDigital = group.get('formatoDigital');
    const ubicacionDigital = group.get('ubicacionDigital');
    const canal = tipoProducto?.canal;
    const isDigital = canal === 'WEB' || canal === 'REDES';

    if (tipoProducto?.requiereDiaTipo) {
      diaTipo?.setValidators([Validators.required]);
    } else {
      diaTipo?.clearValidators();
    }

    if (tipoProducto?.requiereFranjaHoraria) {
      horaDesde?.setValidators([Validators.required]);
      horaHasta?.setValidators([Validators.required]);
    } else {
      horaDesde?.clearValidators();
      horaHasta?.clearValidators();
    }

    if (isDigital && tipoProducto?.requiereFormatoDigital) {
      formatoDigital?.setValidators([Validators.required]);
    } else {
      formatoDigital?.clearValidators();
    }

    if (isDigital && tipoProducto?.requiereUbicacionDigital) {
      ubicacionDigital?.setValidators([Validators.required]);
    } else {
      ubicacionDigital?.clearValidators();
    }

    diaTipo?.updateValueAndValidity({ emitEvent: false });
    horaDesde?.updateValueAndValidity({ emitEvent: false });
    horaHasta?.updateValueAndValidity({ emitEvent: false });
    formatoDigital?.updateValueAndValidity({ emitEvent: false });
    ubicacionDigital?.updateValueAndValidity({ emitEvent: false });
  }

  onSubmit(): void {
    console.log('[TARIFA_DETALLE_EDIT] submit click');
    this.detallesInvalid.set(false);
    this.refreshDetalleAnalysis();

    if (this.hasWarningIssues()) {
      this.showWarningsDialog.set(true);
      return;
    }

    this.saveTarifa();
  }

  confirmSaveWithWarnings(): void {
    this.showWarningsDialog.set(false);
    this.saveTarifa();
  }

  cancelSaveWithWarnings(): void {
    this.showWarningsDialog.set(false);
  }

  private saveTarifa(): void {
    const codigo = this.tarifaForm.get('codigo');
    const nombre = this.tarifaForm.get('nombre');
    const vigenciaDesde = this.tarifaForm.get('vigenciaDesde');
    const vigenciaHasta = this.tarifaForm.get('vigenciaHasta');

    if (codigo?.invalid || nombre?.invalid || vigenciaDesde?.invalid || vigenciaHasta?.invalid) {
      if (environment.pricingDebug) {
        console.debug('[TARIFA_DETALLE_EDIT] tarifaForm invalid', this.getInvalidControls(this.tarifaForm));
      }
      codigo?.markAsTouched();
      nombre?.markAsTouched();
      vigenciaDesde?.markAsTouched();
      vigenciaHasta?.markAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.tarifaForm.value;

    const tarifa: Tarifa = {
      codigo: formValue.codigo,
      nombre: formValue.nombre,
      descripcion: formValue.descripcion || '',
      vigenciaDesde: formValue.vigenciaDesde ? new Date(formValue.vigenciaDesde).toISOString() : undefined,
      vigenciaHasta: formValue.vigenciaHasta ? new Date(formValue.vigenciaHasta).toISOString() : undefined,
      active: formValue.active ?? true
    };
    if (environment.pricingDebug) {
      console.debug('[TARIFA_DETALLE_EDIT] payload', tarifa);
    }

    this.loading.set(true);

    const operation = this.isEditMode()
      ? this.tarifaService.patchTarifaCabecera(this.tarifaId!, tarifa)
      : this.tarifaService.create(tarifa);

    operation
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.refreshIssues();
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: this.isEditMode()
              ? 'Tarifa actualizada correctamente'
              : 'Tarifa creada correctamente',
            life: 3000
          });
          setTimeout(() => {
            this.router.navigate(['/tarifas']);
          }, 1500);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al guardar tarifa:', error);

          let errorMessage = 'Error al guardar la tarifa';
          if (error.error?.violations) {
            const violations = error.error.violations;
            errorMessage = violations.map((v: any) => v.message).join(', ');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.status === 401) {
            errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
          } else if (error.status === 403) {
            errorMessage = 'No tienes permisos para realizar esta acción.';
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000
          });
        }
      });
  }

  getDetalleRows(): Array<{ group: FormGroup; index: number }> {
    return this.tarifaDetallesFormArray.controls
      .map((control, index) => ({ group: control as FormGroup, index }))
      .sort((a, b) => {
        const prioridadA = Number(a.group.get('prioridad')?.value ?? 0);
        const prioridadB = Number(b.group.get('prioridad')?.value ?? 0);
        if (prioridadA !== prioridadB) {
          return prioridadA - prioridadB;
        }
        return a.index - b.index;
      });
  }

  getDetalleRowsForTipo(tipoCodigo: string | null): Array<{ group: FormGroup; index: number }> {
    if (!tipoCodigo) {
      return [];
    }
    return this.getDetalleRows().filter((row) => {
      const tipo = this.getDetalleTipoProducto(row.group);
      if (!tipo?.codigo) {
        return tipoCodigo === '__SIN_TIPO__';
      }
      return tipo.codigo === tipoCodigo;
    });
  }

  getDetalleRowsForTipoFiltered(tipoCodigo: string | null): Array<{ group: FormGroup; index: number }> {
    const rows = this.getDetalleRowsForTipo(tipoCodigo);
    if (!tipoCodigo) return rows;
    if (!this.inconsistenciesOnlyByTipo()[tipoCodigo]) {
      return rows;
    }
    const issueIds = this.getServerIssueDetalleIdsForTipo(tipoCodigo);
    const selected = this.selectedDetalleIndex();
    return rows.filter(row => {
      const id = row.group.get('id')?.value ?? null;
      if (row.index === selected) return true;
      if (!id) return true; // aún no confirmado por servidor
      if (row.group.dirty) return true; // pendiente de guardar
      return issueIds.has(Number(id));
    });
  }

  selectTipoTab(tipoCodigo: string): void {
    this.activeTipoCodigo.set(tipoCodigo);
    const rows = this.getDetalleRowsForTipoFiltered(tipoCodigo);
    if (rows.length > 0) {
      this.selectDetalle(rows[0].index);
    } else {
      this.selectedDetalleIndex.set(null);
    }
  }

  getActiveTab(): TarifaTipoTab | null {
    const active = this.activeTipoCodigo();
    return this.tipoTabs().find(tab => tab.codigo === active) ?? null;
  }

  addDetalleForTipo(tipoCodigo: string | null): void {
    if (!tipoCodigo || tipoCodigo === '__SIN_TIPO__') {
      return;
    }
    const tipoProducto = this.tipoProductoOptions().find(tipo => tipo.codigo === tipoCodigo) ?? undefined;
    const defaultDiaTipo = tipoCodigo === 'AIRE_SPOT' ? 'LUN_VIE' : null;
    const group = this.createDetalleGroup({
      tipoProducto: tipoProducto ?? undefined,
      unidadTarifacion: tipoProducto?.unidadTarifacion ?? undefined,
      precioUnitario: undefined,
      prioridad: 0,
      diaTipo: defaultDiaTipo,
      horaDesde: null,
      horaHasta: null,
      formatoDigital: undefined,
      ubicacionDigital: undefined,
      plataformaRedes: null
    });
    this.tarifaDetallesFormArray.push(group);
    this.selectDetalle(this.tarifaDetallesFormArray.length - 1);
    this.refreshDetalleAnalysis();
  }

  getTabIssues(tipoCodigo: string): TarifaDetalleAnalyzerIssue[] {
    const analysis = this.detalleAnalysis()[tipoCodigo];
    return analysis?.issues ?? [];
  }

  getActiveIssues(): TarifaDetalleAnalyzerIssue[] {
    const tipo = this.activeTipoCodigo();
    if (!tipo) return [];
    return this.getTabIssues(tipo).sort((a, b) => a.rowIndex - b.rowIndex);
  }

  getGroupedIssues(): GroupedIssue[] {
    const tipo = this.activeTipoCodigo();
    if (!tipo) return [];
    const snapshot = this.serverIssues();
    const byTipo = snapshot?.byTipoProducto?.find(t => t.tipoProductoCodigo === tipo);
    if (!byTipo) return [];

    const toRowIndex = (detalleId: number | null): number | null => {
      if (!detalleId) return null;
      const idx = this.tarifaDetallesFormArray.controls.findIndex(c => String((c as FormGroup).get('id')?.value) === String(detalleId));
      return idx >= 0 ? idx : null;
    };

    const vms: GroupedIssue[] = [];

    (byTipo.issues ?? []).forEach((issue) => {
      if (issue.issueType === 'REQUIRED') {
        const rowIndex = toRowIndex(issue.detalleId);
        if (rowIndex === null) return;
        vms.push({
          key: `REQ:${issue.detalleId ?? 'x'}:${issue.key}`,
          type: 'ERROR',
          code: 'INV',
          rowIndex,
          detalleId: issue.detalleId ?? null,
          fields: issue.key ? [issue.key] : [],
          message: issue.message,
          relatedRowIndexes: [],
          count: 0
        });
      }

      if (issue.issueType === 'DUPLICATE') {
        const ids = issue.duplicateGroup?.detalleIds ?? [];
        const rowIndexes = ids.map(id => toRowIndex(id)).filter((v): v is number => v !== null);
        if (rowIndexes.length < 2) return;
        const [main, ...related] = rowIndexes;
        vms.push({
          key: `DUP:${issue.duplicateGroup?.key ?? issue.key}`,
          type: 'WARN',
          code: 'DUPLICATE',
          rowIndex: main,
          detalleId: null,
          fields: [],
          message: issue.message,
          relatedRowIndexes: related,
          count: related.length
        });
      }
    });

    return vms.sort((a, b) => a.rowIndex - b.rowIndex);
  }

  getWarningIssues(): TarifaDetalleAnalyzerIssue[] {
    return Object.values(this.detalleAnalysis())
      .flatMap(result => result?.issues ?? [])
      .filter(issue => issue.code === 'OVERLAP');
  }

  getRowMatchingKey(index: number): string {
    const tipo = this.activeTipoCodigo();
    if (!tipo) return '';
    const analysis = this.detalleAnalysis()[tipo];
    return analysis?.keyByRow?.[index] ?? '';
  }

  rowHasError(index: number): boolean {
    return this.getRowIssues(index, 'ERROR').length > 0;
  }

  rowHasWarning(index: number): boolean {
    return this.getRowIssues(index, 'WARN').length > 0;
  }

  rowIsHighlighted(index: number): boolean {
    return this.highlightedRows().has(index);
  }

  private getRowIssues(index: number, type?: 'ERROR' | 'WARN'): TarifaDetalleAnalyzerIssue[] {
    const tipo = this.activeTipoCodigo();
    if (!tipo) return [];
    const issues = this.detalleAnalysis()[tipo]?.issues ?? [];
    return issues.filter(issue => issue.rowIndex === index && (!type || issue.type === type));
  }

  getTabCounts(tipoCodigo: string): { total: number; invalid: number; overlaps: number; duplicates: number } {
    const rows = this.getDetalleRowsForTipo(tipoCodigo);
    const issues = this.detalleAnalysis()[tipoCodigo]?.issues ?? [];
    const overlapRows = new Set(issues.filter(i => i.code === 'OVERLAP').map(i => i.rowIndex));
    const snapshot = this.serverIssues();
    const byTipo = snapshot?.byTipoProducto?.find(t => t.tipoProductoCodigo === tipoCodigo);
    return {
      total: rows.length,
      invalid: byTipo?.invCount ?? 0,
      overlaps: overlapRows.size,
      duplicates: byTipo?.dupCount ?? 0
    };
  }

  formatIssueFields(issue: TarifaDetalleAnalyzerIssue): string {
    if (!issue.fields?.length) {
      return '-';
    }
    return issue.fields.map(field => this.detalleFieldLabels[field] ?? field).join(', ');
  }

  hasBlockingIssues(): boolean {
    return false;
  }

  hasWarningIssues(): boolean {
    return Object.values(this.detalleAnalysis()).some(result => result?.warningCount > 0);
  }

  focusIssue(issue: TarifaDetalleAnalyzerIssue): void {
    this.focusGroupedIssue({
      key: `${issue.detalleId ?? `row${issue.rowIndex}`}:${issue.code}`,
      type: issue.type,
      code: issue.code,
      rowIndex: issue.rowIndex,
      detalleId: issue.detalleId ?? null,
      fields: issue.fields ?? [],
      message: issue.message,
      relatedRowIndexes: issue.relatedRowIndexes ?? [],
      count: issue.relatedRowIndexes?.length ?? 0
    });
  }

  focusGroupedIssue(issue: GroupedIssue, targetRowIndex?: number): void {
    const mainIndex = issue.rowIndex;
    const focusIndex = targetRowIndex ?? mainIndex;
    const group = this.tarifaDetallesFormArray.at(focusIndex) as FormGroup;
    const tipoCodigo = this.getDetalleTipoProducto(group)?.codigo ?? this.activeTipoCodigo() ?? '';
    if (tipoCodigo && tipoCodigo !== this.activeTipoCodigo()) {
      this.selectTipoTab(tipoCodigo);
    }
    this.selectDetalle(focusIndex);
    const toHighlight = [mainIndex, ...(issue.relatedRowIndexes ?? [])];
    this.applyHighlight(toHighlight);
    this.handleHiddenCounterparts(issue, tipoCodigo);
    setTimeout(() => {
      const row = document.querySelector(`[data-detalle-index=\"${focusIndex}\"]`);
      row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const field = issue.fields?.[0];
      if (field) {
        const target = document.querySelector(`[data-detalle-field=\"${field}\"] input, [data-detalle-field=\"${field}\"] textarea, [data-detalle-field=\"${field}\"] .p-inputtext, [data-detalle-field=\"${field}\"] .p-inputnumber-input`);
        if (target instanceof HTMLElement) {
          target.focus();
        }
      }
    }, 0);
  }

  toggleIssueGroup(issue: GroupedIssue): void {
    const current = { ...this.expandedIssueGroups() };
    current[issue.key] = !current[issue.key];
    this.expandedIssueGroups.set(current);
  }

  isIssueGroupExpanded(issue: GroupedIssue): boolean {
    return !!this.expandedIssueGroups()[issue.key];
  }

  toggleInconsistencies(tipoCodigo: string | null): void {
    if (!tipoCodigo) return;
    const current = { ...this.inconsistenciesOnlyByTipo() };
    current[tipoCodigo] = !current[tipoCodigo];
    this.inconsistenciesOnlyByTipo.set(current);
    console.debug(`[TARIFA_UI] filterOnlyIssues=${current[tipoCodigo]} visibleRows=${this.getDetalleRowsForTipoFiltered(tipoCodigo).length}`);
    this.handleSelectionAfterFilter(tipoCodigo);
  }

  getInconsistenciesToggle(tipoCodigo: string | null): boolean {
    if (!tipoCodigo) return false;
    return !!this.inconsistenciesOnlyByTipo()[tipoCodigo];
  }

  getInconsistentRowIndexes(tipoCodigo: string): Set<number> {
    const issueIds = this.getServerIssueDetalleIdsForTipo(tipoCodigo);
    const indexes = new Set<number>();
    this.getDetalleRowsForTipo(tipoCodigo).forEach(row => {
      const id = row.group.get('id')?.value ?? null;
      if (id && issueIds.has(Number(id))) {
        indexes.add(row.index);
      }
    });
    return indexes;
  }

  getFilterCounts(tipoCodigo: string | null): { shown: number; total: number } {
    const total = this.getDetalleRowsForTipo(tipoCodigo).length;
    const shown = this.getDetalleRowsForTipoFiltered(tipoCodigo).length;
    return { shown, total };
  }

  openResolveDuplicate(issue: GroupedIssue): void {
    const ids = this.getDuplicateIdsFromIssue(issue);
    if (ids.length < 2) {
      return;
    }
    this.resolveKey.set(this.getRowMatchingKey(issue.rowIndex) || issue.key || '');
    this.resolveDetalleIds.set(ids);
    this.resolveAId.set(ids[0] ?? null);
    this.resolveBId.set(ids[1] ?? null);
    this.resolveDialogVisible.set(true);
  }

  closeResolveDuplicate(): void {
    this.resolveDialogVisible.set(false);
    this.resolveKey.set('');
    this.resolveDetalleIds.set([]);
    this.resolveAId.set(null);
    this.resolveBId.set(null);
  }

  getResolveDetalleSnapshot(id: number | null): TarifaDetalle | null {
    if (!id) return null;
    const idx = this.tarifaDetallesFormArray.controls.findIndex(c => String((c as FormGroup).get('id')?.value) === String(id));
    if (idx < 0) return null;
    const group = this.tarifaDetallesFormArray.at(idx) as FormGroup;
    return this.buildDetalleSnapshot(group);
  }

  resolveDeleteDetalle(id: number | null): void {
    if (!id) return;
    const idx = this.tarifaDetallesFormArray.controls.findIndex(c => String((c as FormGroup).get('id')?.value) === String(id));
    if (idx < 0) return;
    this.deleteDetalle(idx);
    this.closeResolveDuplicate();
  }

  trackDetalleRow(_: number, row: { index: number }): number {
    return row.index;
  }

  trackTipoTab(_: number, tab: TarifaTipoTab): string {
    return tab.codigo;
  }

  trackIssue(index: number, issue: { code: string; rowIndex: number; fields?: string[]; key?: string }): string | number {
    return issue.key ?? `${issue.code}-${issue.rowIndex}-${issue.fields?.join('-') ?? index}`;
  }

  getSelectedDetalleForm(): FormGroup | null {
    const index = this.selectedDetalleIndex();
    if (index === null || index === undefined) {
      return null;
    }
    return this.tarifaDetallesFormArray.at(index) as FormGroup;
  }

  getDetalleTipoProducto(group: FormGroup): TipoProducto | null {
    const id = group.get('tipoProductoId')?.value;
    return this.getTipoProductoById(id, group);
  }

  isCanalAire(group: FormGroup): boolean {
    return this.getDetalleTipoProducto(group)?.canal === 'AIRE';
  }

  isCanalDigital(group: FormGroup): boolean {
    const canal = this.getDetalleTipoProducto(group)?.canal;
    return canal === 'WEB' || canal === 'REDES';
  }

  isCanalRedes(group: FormGroup): boolean {
    return this.getDetalleTipoProducto(group)?.canal === 'REDES';
  }

  getDetalleChips(group: FormGroup): string[] {
    const chips: string[] = [];
    const tipoProducto = this.getDetalleTipoProducto(group);
    const canal = tipoProducto?.canal;

    if (canal === 'AIRE') {
      const diaTipo = group.get('diaTipo')?.value;
      const horaDesde = this.formatTime(group.get('horaDesde')?.value);
      const horaHasta = this.formatTime(group.get('horaHasta')?.value);
      const programa = group.get('programa')?.value;
      const tipoMaterial = group.get('tipoMaterial')?.value;

      if (diaTipo) chips.push(`Día ${this.mapDiaTipoLabel(diaTipo)}`);
      if (horaDesde && horaHasta) chips.push(`Hora ${horaDesde}–${horaHasta}`);
      if (programa?.nombre) chips.push(`Programa ${programa.nombre}`);
      if (tipoMaterial?.nombre) chips.push(`Material ${tipoMaterial.nombre}`);
    }

    if (canal === 'WEB' || canal === 'REDES') {
      const formato = group.get('formatoDigital')?.value;
      const ubicacion = group.get('ubicacionDigital')?.value;
      const plataforma = group.get('plataformaRedes')?.value;

      if (formato?.codigo || formato?.nombre) {
        chips.push(`Formato ${formato.codigo || formato.nombre}`);
      }
      if (ubicacion?.codigo || ubicacion?.nombre) {
        chips.push(`Ubicación ${ubicacion.codigo || ubicacion.nombre}`);
      }
      if (canal === 'REDES' && plataforma) {
        chips.push(`Plataforma ${plataforma}`);
      }
    }

    return chips.length ? chips : ['Genérica'];
  }

  getDetalleChipsFromSnapshot(detalle: TarifaDetalle): string[] {
    const chips: string[] = [];
    const tipoProducto: any = detalle.tipoProducto as any;
    const canal = tipoProducto?.canal;

    if (canal === 'AIRE') {
      const diaTipo = detalle.diaTipo ?? null;
      const horaDesde = this.formatTime(detalle.horaDesde);
      const horaHasta = this.formatTime(detalle.horaHasta);
      const programa: any = detalle.programa as any;
      const tipoMaterial: any = detalle.tipoMaterial as any;

      if (diaTipo) chips.push(`Día ${this.mapDiaTipoLabel(String(diaTipo))}`);
      if (horaDesde && horaHasta) chips.push(`Hora ${horaDesde}–${horaHasta}`);
      if (programa?.nombre) chips.push(`Programa ${programa.nombre}`);
      if (tipoMaterial?.nombre) chips.push(`Material ${tipoMaterial.nombre}`);
    }

    if (canal === 'WEB' || canal === 'REDES') {
      const formato: any = detalle.formatoDigital as any;
      const ubicacion: any = detalle.ubicacionDigital as any;
      const plataforma = detalle.plataformaRedes ?? null;

      if (formato?.codigo || formato?.nombre) {
        chips.push(`Formato ${formato.codigo || formato.nombre}`);
      }
      if (ubicacion?.codigo || ubicacion?.nombre) {
        chips.push(`Ubicación ${ubicacion.codigo || ubicacion.nombre}`);
      }
      if (canal === 'REDES' && plataforma) {
        chips.push(`Plataforma ${plataforma}`);
      }
    }

    return chips.length ? chips : ['Genérica'];
  }

  getDetalleMissingFields(group: FormGroup): string[] {
    const missing: string[] = [];
    const tipoProducto = this.getDetalleTipoProducto(group);
    if (!tipoProducto) {
      missing.push('Tipo producto');
      return missing;
    }
    if (tipoProducto.requiereDiaTipo && !group.get('diaTipo')?.value) {
      missing.push('Día grupo');
    }
    if (tipoProducto.requiereFranjaHoraria) {
      if (!group.get('horaDesde')?.value || !group.get('horaHasta')?.value) {
        missing.push('Hora desde/hasta');
      }
    }
    const canal = tipoProducto?.canal;
    const isDigital = canal === 'WEB' || canal === 'REDES';
    if (isDigital && tipoProducto.requiereFormatoDigital && !group.get('formatoDigital')?.value) {
      missing.push('Formato');
    }
    if (isDigital && tipoProducto.requiereUbicacionDigital && !group.get('ubicacionDigital')?.value) {
      missing.push('Ubicación');
    }
    return missing;
  }

  isDetalleIncomplete(group: FormGroup): boolean {
    return this.getDetalleMissingFields(group).length > 0;
  }

  getDetalleMissingMessage(group: FormGroup): string {
    const missing = this.getDetalleMissingFields(group);
    return missing.length ? `Falta: ${missing.join(', ')}` : '';
  }

  shouldShowDetalleFieldError(group: FormGroup, field: string): boolean {
    const control = group.get(field);
    if (!control) return false;
    const index = this.getDetalleIndex(group);
    const hasIssue = this.hasIssueForField(index, field);
    return control.invalid && (control.touched || hasIssue) || hasIssue;
  }

  getDetalleFieldErrorMessage(group: FormGroup, field: string): string {
    const control = group.get(field);
    if (!control) return '';
    if (control.hasError('required')) {
      return 'Campo requerido';
    }
    if (control.hasError('min')) {
      return 'Debe ser mayor o igual a 0';
    }
    if (control.hasError('maxlength')) {
      const maxLength = control.errors?.['maxlength']?.requiredLength ?? 0;
      return maxLength ? `Máximo ${maxLength} caracteres` : 'Longitud inválida';
    }
    return this.detalleFieldLabels[field] ?? 'Valor inválido';
  }

  private hasIssueForField(index: number, field: string): boolean {
    const tipo = this.activeTipoCodigo();
    if (!tipo) return false;
    const issues = this.detalleAnalysis()[tipo]?.issues ?? [];
    return issues.some(issue => issue.rowIndex === index && issue.fields?.includes(field));
  }

  private getDetalleIndex(group: FormGroup): number {
    return this.tarifaDetallesFormArray.controls.indexOf(group);
  }

  formatEntityLabel(value: any): string {
    if (!value) return '-';
    if (typeof value === 'string') return value;
    return value.nombre || value.codigo || value.displayName || (value.id ? `#${value.id}` : '-');
  }

  private hasDetallesIncomplete(): boolean {
    return this.tarifaDetallesFormArray.controls.some(control =>
      this.isDetalleIncomplete(control as FormGroup)
    );
  }

  private mapDiaTipoLabel(value: string): string {
    return this.diaTipoOptions.find(option => option.value === value)?.label ?? value;
  }

  private createDetalleDraftForm(): FormGroup {
    const form = this.fb.group({
      tipoProductoId: [null, Validators.required],
      precioUnitario: [null, [Validators.required, Validators.min(0)]],
      prioridad: [0],
      diaTipo: [null],
      horaDesde: [null],
      horaHasta: [null],
      formatoDigital: [null],
      ubicacionDigital: [null],
      plataformaRedes: [null]
    });

    form.get('tipoProductoId')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const tipoProducto = this.getTipoProductoById(value);
      const canal = tipoProducto?.canal;
      const isDigital = canal === 'WEB' || canal === 'REDES';
      if (tipoProducto?.requiereDiaTipo) {
        form.get('diaTipo')?.setValidators([Validators.required]);
      } else {
        form.get('diaTipo')?.clearValidators();
      }

      if (tipoProducto?.requiereFranjaHoraria) {
        form.get('horaDesde')?.setValidators([Validators.required]);
        form.get('horaHasta')?.setValidators([Validators.required]);
      } else {
        form.get('horaDesde')?.clearValidators();
        form.get('horaHasta')?.clearValidators();
      }

      if (isDigital && tipoProducto?.requiereFormatoDigital) {
        form.get('formatoDigital')?.setValidators([Validators.required]);
      } else {
        form.get('formatoDigital')?.clearValidators();
      }

      if (isDigital && tipoProducto?.requiereUbicacionDigital) {
        form.get('ubicacionDigital')?.setValidators([Validators.required]);
      } else {
        form.get('ubicacionDigital')?.clearValidators();
      }

      form.get('diaTipo')?.updateValueAndValidity({ emitEvent: false });
      form.get('horaDesde')?.updateValueAndValidity({ emitEvent: false });
      form.get('horaHasta')?.updateValueAndValidity({ emitEvent: false });
      form.get('formatoDigital')?.updateValueAndValidity({ emitEvent: false });
      form.get('ubicacionDigital')?.updateValueAndValidity({ emitEvent: false });
    });

    return form;
  }

  getDetalleDraftTipoProducto(): TipoProducto | null {
    const value = this.detalleDraftForm.get('tipoProductoId')?.value;
    return this.getTipoProductoById(value);
  }

  private buildDetallesPayload(): TarifaDetalle[] {
    return this.tarifaDetallesFormArray.controls.map(control => {
      return this.buildTarifaDetallePayload(control.value);
    });
  }

  onCancel(): void {
    this.router.navigate(['/tarifas']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  hasError(fieldName: string, errorType: string): boolean {
    const field = this.tarifaForm.get(fieldName);
    return !!(field?.hasError(errorType) && field?.touched);
  }

  getErrorMessage(fieldName: string): string {
    const field = this.tarifaForm.get(fieldName);
    if (field?.hasError('required')) {
      return 'Este campo es requerido';
    }
    if (field?.hasError('maxlength')) {
      const maxLength = field.errors?.['maxlength'].requiredLength;
      return `Máximo ${maxLength} caracteres`;
    }
    return '';
  }

  private loadTipoProductos(): void {
    const params = new HttpParams()
      .set('pagination', 'false')
      .set('active', 'true');
    this.tipoProductoService.getAll(params).subscribe({
      next: (resp: any) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        const mapped = members.map((item: any) => ({
          ...item,
          id: item?.id !== undefined && item?.id !== null ? String(item.id) : item?.id
        }));
        this.tipoProductoOptions.set(mapped);
        this.syncTabs();
        this.refreshDetalleAnalysis();
      },
      error: () => {
        this.tipoProductoOptions.set([]);
        this.syncTabs();
      }
    });
  }

  private setupDetalleAnalysis(): void {
    this.tarifaDetallesFormArray.valueChanges
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => this.refreshDetalleAnalysis());
  }

  private syncTabs(): void {
    const options = this.tipoProductoOptions();
    const tabs: TarifaTipoTab[] = this.tipoTabConfig
      .map((config) => ({
        codigo: config.codigo,
        label: config.label,
        tipoProducto: options.find(tipo => tipo.codigo === config.codigo) ?? null
      }))
      .filter(tab => tab.tipoProducto !== null || this.hasDetallesForCodigo(tab.codigo));

    if (this.tarifaDetallesFormArray.controls.some(control => !this.getDetalleTipoProducto(control as FormGroup))) {
      tabs.push({ codigo: '__SIN_TIPO__', label: 'Sin tipo', tipoProducto: null, disabled: true });
    }

    this.tipoTabs.set(tabs);
    const toggleState = { ...this.inconsistenciesOnlyByTipo() };
    tabs.forEach(tab => {
      if (toggleState[tab.codigo] === undefined) {
        toggleState[tab.codigo] = false;
      }
    });
    this.inconsistenciesOnlyByTipo.set(toggleState);
    const active = this.activeTipoCodigo();
    if (!active || !tabs.find(tab => tab.codigo === active)) {
      const fallback = tabs[0]?.codigo ?? null;
      this.activeTipoCodigo.set(fallback);
    }
  }

  private hasDetallesForCodigo(tipoCodigo: string): boolean {
    return this.tarifaDetallesFormArray.controls.some(control => {
      const tipo = this.getDetalleTipoProducto(control as FormGroup);
      return tipo?.codigo === tipoCodigo;
    });
  }

  private refreshDetalleAnalysis(): void {
    const analysis: Record<string, TarifaDetalleAnalyzerResult> = {};
    this.tipoTabs().forEach(tab => {
      const rows = this.getDetalleRowsForTipo(tab.codigo).map(row => ({
        index: row.index,
        detalle: this.buildDetalleSnapshot(row.group),
        tipoProducto: this.getDetalleTipoProducto(row.group)
      }));
      analysis[tab.codigo] = analyzeTarifaDetalles(rows);
    });
    this.detalleAnalysis.set(analysis);
    const active = this.activeTipoCodigo();
    if (active) {
      this.handleSelectionAfterFilter(active);
    }
    if (environment.pricingDebug) {
      const activeLog = this.activeTipoCodigo() ?? '-';
      const activeIssues = analysis[activeLog]?.issues ?? [];
      console.debug('[TARIFA_DETALLE_EDITOR]', {
        activeTipo: activeLog,
        tabs: Object.keys(analysis),
        issuesCount: activeIssues.length,
        errors: analysis[activeLog]?.errorCount ?? 0,
        warnings: analysis[activeLog]?.warningCount ?? 0,
        topIssues: activeIssues.slice(0, 3).map(issue => ({
          type: issue.type,
          code: issue.code,
          row: issue.rowIndex,
          message: issue.message
        }))
      });
    }
  }

  private handleSelectionAfterFilter(tipoCodigo: string): void {
    if (!this.inconsistenciesOnlyByTipo()[tipoCodigo]) {
      return;
    }
    const filtered = this.getDetalleRowsForTipoFiltered(tipoCodigo);
    const selected = this.selectedDetalleIndex();
    if (selected === null) {
      if (filtered.length > 0) {
        this.selectDetalle(filtered[0].index);
      }
      return;
    }
    const stillVisible = filtered.some(row => row.index === selected);
    if (!stillVisible) {
      if (filtered.length > 0) {
        const next = filtered.find(row => row.index > selected) ?? filtered[0];
        this.selectDetalle(next.index);
      } else {
        this.selectedDetalleIndex.set(null);
      }
    }
  }

  private applyHighlight(indexes: number[]): void {
    const unique = new Set(indexes.filter(index => index !== null && index !== undefined));
    this.highlightedRows.set(unique);
    setTimeout(() => this.highlightedRows.set(new Set()), 2000);
  }

  private handleHiddenCounterparts(issue: GroupedIssue, tipoCodigo: string): void {
    if (!issue.relatedRowIndexes?.length) return;
    const filteredRows = this.getDetalleRowsForTipoFiltered(tipoCodigo).map(row => row.index);
    const visibleSet = new Set(filteredRows);
    const hidden = issue.relatedRowIndexes.filter(index => !visibleSet.has(index));
    if (hidden.length > 0 && this.getInconsistenciesToggle(tipoCodigo)) {
      this.messageService.add({
        severity: 'info',
        summary: 'Contraparte no visible',
        detail: 'La contraparte no es inconsistente. Desactivá el filtro para verla.',
        life: 5000
      });
    }
    const otherTipo = issue.relatedRowIndexes
      .map(index => this.getDetalleTipoProducto(this.tarifaDetallesFormArray.at(index) as FormGroup)?.codigo)
      .find(code => code && code !== tipoCodigo);
    if (otherTipo && otherTipo !== tipoCodigo) {
      const tab = this.tipoTabs().find(t => t.codigo === otherTipo);
      if (tab) {
        this.messageService.add({
          severity: 'info',
          summary: 'Contraparte en otro tab',
          detail: `Está en el tab ${tab.label}. Cambiá de tab para verla.`,
          life: 5000
        });
      }
    }
  }

  private buildDetalleSnapshot(group: FormGroup): TarifaDetalle {
    return {
      id: group.get('id')?.value ?? undefined,
      tipoProducto: this.getDetalleTipoProducto(group) ?? undefined,
      unidadTarifacion: group.get('unidadTarifacion')?.value ?? null,
      precioUnitario: group.get('precioUnitario')?.value ?? null,
      prioridad: group.get('prioridad')?.value ?? 0,
      diaTipo: group.get('diaTipo')?.value ?? null,
      horaDesde: group.get('horaDesde')?.value ?? null,
      horaHasta: group.get('horaHasta')?.value ?? null,
      programa: group.get('programa')?.value ?? null,
      tipoMaterial: group.get('tipoMaterial')?.value ?? null,
      formatoDigital: group.get('formatoDigital')?.value ?? null,
      ubicacionDigital: group.get('ubicacionDigital')?.value ?? null,
      plataformaRedes: group.get('plataformaRedes')?.value ?? null
    };
  }

  searchProgramas(event: any): void {
    const query = event.query || '';
    const params = new HttpParams()
      .set('q', query)
      .set('pagination', 'false')
      .set('active', 'true');
    this.programaService.searchProgramas(params).subscribe({
      next: (resp: any) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        const mapped = members.map((programa: any) => ({
          ...programa,
          id: programa?.id !== undefined && programa?.id !== null ? String(programa.id) : programa?.id,
          displayName: `${programa.codigo || ''} - ${programa.nombre || ''}`.trim()
        }));
        this.programaSuggestions.set(mapped);
      },
      error: () => {
        this.programaSuggestions.set([]);
      }
    });
  }

  searchTipoMaterial(event: any): void {
    const query = event.query || '';
    const params = new HttpParams()
      .set('q', query)
      .set('pagination', 'false')
      .set('active', 'true');
    this.tipoMaterialService.getAll(params).subscribe({
      next: (resp: any) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        const mapped = members.map((tipoMaterial: any) => ({
          ...tipoMaterial,
          id: tipoMaterial?.id !== undefined && tipoMaterial?.id !== null ? String(tipoMaterial.id) : tipoMaterial?.id,
          displayName: `${tipoMaterial.codigo || ''} - ${tipoMaterial.nombre || ''}`.trim()
        }));
        this.tipoMaterialSuggestions.set(mapped);
      },
      error: () => {
        this.tipoMaterialSuggestions.set([]);
      }
    });
  }

  searchFormatoDigital(event: any): void {
    const query = event.query || '';
    const params = new HttpParams()
      .set('q', query)
      .set('pagination', 'false')
      .set('active', 'true');
    this.formatoDigitalService.searchFormatos(params).subscribe({
      next: (resp: any) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        const mapped = members.map((formato: any) => ({
          ...formato,
          id: formato?.id !== undefined && formato?.id !== null ? String(formato.id) : formato?.id,
          displayName: `${formato.codigo || ''} - ${formato.nombre || ''}`.trim()
        }));
        this.formatoDigitalSuggestions.set(mapped);
      },
      error: () => {
        this.formatoDigitalSuggestions.set([]);
      }
    });
  }

  searchUbicacionDigital(event: any): void {
    const query = event.query || '';
    const params = new HttpParams()
      .set('q', query)
      .set('pagination', 'false')
      .set('active', 'true');
    this.ubicacionDigitalService.searchUbicaciones(params).subscribe({
      next: (resp: any) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        const mapped = members.map((ubicacion: any) => ({
          ...ubicacion,
          id: ubicacion?.id !== undefined && ubicacion?.id !== null ? String(ubicacion.id) : ubicacion?.id,
          displayName: `${ubicacion.codigo || ''} - ${ubicacion.nombre || ''}`.trim()
        }));
        this.ubicacionDigitalSuggestions.set(mapped);
      },
      error: () => {
        this.ubicacionDigitalSuggestions.set([]);
      }
    });
  }

  private hydrateDetalleIris(group: FormGroup): void {
    this.hydrateEntityControl(
      group,
      'tipoMaterial',
      (id) => this.tipoMaterialService.getById(id),
      (entity) => this.withDisplayName(entity),
      this.tipoMaterialSuggestions
    );
    this.hydrateEntityControl(
      group,
      'programa',
      (id) => this.programaService.getById(id),
      (entity) => this.withDisplayName(entity),
      this.programaSuggestions
    );
    this.hydrateEntityControl(
      group,
      'formatoDigital',
      (id) => this.formatoDigitalService.getById(id),
      (entity) => this.withDisplayName(entity),
      this.formatoDigitalSuggestions
    );
    this.hydrateEntityControl(
      group,
      'ubicacionDigital',
      (id) => this.ubicacionDigitalService.getById(id),
      (entity) => this.withDisplayName(entity),
      this.ubicacionDigitalSuggestions
    );
  }

  private hydrateEntityControl(
    group: FormGroup,
    controlName: string,
    loader: (id: string | number) => Observable<any>,
    mapToDisplay: (entity: any) => any,
    suggestionsSignal: any
  ): void {
    const control = group.get(controlName);
    if (!control) return;
    const value = control.value;
    if (!value) return;

    const id = this.extractId(value);
    if (!id) {
      if (typeof value === 'object') {
        const mapped = this.withDisplayName(value);
        control.patchValue(mapped, { emitEvent: false });
        this.ensureSuggestionItem(suggestionsSignal, mapped);
      }
      return;
    }

    if (typeof value === 'object' && (value.displayName || value.codigo || value.nombre)) {
      const mapped = this.withDisplayName(value);
      control.patchValue(mapped, { emitEvent: false });
      this.ensureSuggestionItem(suggestionsSignal, mapped);
      return;
    }

    const cacheKey = `${controlName}:${id}`;
    if (this.hydratedEntityCache.has(cacheKey)) {
      const cached = this.hydratedEntityCache.get(cacheKey);
      control.patchValue(cached, { emitEvent: false });
      this.ensureSuggestionItem(suggestionsSignal, cached);
      return;
    }

    loader(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (entity) => {
          const mapped = mapToDisplay(entity);
          this.hydratedEntityCache.set(cacheKey, mapped);
          control.patchValue(mapped, { emitEvent: false });
          this.ensureSuggestionItem(suggestionsSignal, mapped);
        },
        error: () => {}
      });
  }

  private toIri(resource: string, id: number | string | null | undefined): string | null {
    if (id === null || id === undefined || id === '') return null;
    return `/api/${resource}/${id}`;
  }

  private buildTarifaDetallePayload(value: any): TarifaDetalle {
    return {
      id: value.id || undefined,
      tipoProducto: this.toIri('tipo-productos', value.tipoProductoId) || undefined,
      unidadTarifacion: value.unidadTarifacion || undefined,
      precioUnitario: this.formatPrecio(value.precioUnitario) ?? undefined,
      prioridad: value.prioridad ?? 0,
      diaTipo: value.diaTipo ?? null,
      horaDesde: this.formatTime(value.horaDesde),
      horaHasta: this.formatTime(value.horaHasta),
      programa: this.toIri('programas', this.extractId(value.programa)) || undefined,
      tipoMaterial: this.toIri('tipo-materials', this.extractId(value.tipoMaterial)) || undefined,
      formatoDigital: this.toIri('formatos-digitales', this.extractId(value.formatoDigital)) || undefined,
      ubicacionDigital: this.toIri('ubicaciones-digitales', this.extractId(value.ubicacionDigital)) || undefined,
      plataformaRedes: value.plataformaRedes ?? null
    };
  }

  private buildTarifaDetalleWritePayload(value: any, tarifaId: string, includeTarifa: boolean): TarifaDetalleWritePayload {
    return {
      tarifa: includeTarifa ? (this.toIri('tarifas', tarifaId) ?? undefined) : undefined,
      tipoProducto: this.toIri('tipo-productos', value.tipoProductoId) ?? undefined,
      unidadTarifacion: value.unidadTarifacion || undefined,
      precioUnitario: this.formatPrecio(value.precioUnitario) ?? undefined,
      prioridad: value.prioridad ?? 0,
      diaTipo: value.diaTipo ?? null,
      horaDesde: this.formatTime(value.horaDesde),
      horaHasta: this.formatTime(value.horaHasta),
      programa: this.toIri('programas', this.extractId(value.programa)),
      tipoMaterial: this.toIri('tipo-materials', this.extractId(value.tipoMaterial)),
      formatoDigital: this.toIri('formatos-digitales', this.extractId(value.formatoDigital)),
      ubicacionDigital: this.toIri('ubicaciones-digitales', this.extractId(value.ubicacionDigital)),
      plataformaRedes: value.plataformaRedes ?? null,
      active: true
    };
  }

  private getServerIssueDetalleIdsForTipo(tipoCodigo: string): Set<number> {
    const snapshot = this.serverIssues();
    const set = new Set<number>();
    if (!snapshot) return set;
    const byTipo = snapshot.byTipoProducto?.find(t => t.tipoProductoCodigo === tipoCodigo);
    if (!byTipo) return set;
    (byTipo.issues ?? []).forEach(issue => {
      if (issue.issueType === 'REQUIRED' && issue.detalleId) {
        set.add(issue.detalleId);
      }
      if (issue.issueType === 'DUPLICATE' && issue.duplicateGroup?.detalleIds?.length) {
        issue.duplicateGroup.detalleIds.forEach((id: number) => set.add(id));
      }
    });
    return set;
  }

  private refreshIssues(): void {
    if (!this.tarifaId || !this.isEditMode()) return;
    this.issuesLoading.set(true);
    this.tarifaService.getIssues(this.tarifaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (snapshot: TarifaIssuesSnapshot) => {
          this.serverIssues.set(snapshot);
          this.issuesLoading.set(false);
          console.debug(`[TARIFA_UI] refreshIssues ok inv=${snapshot.invCount} dup=${snapshot.dupCount}`);
        },
        error: (error) => {
          this.issuesLoading.set(false);
          console.error('[TARIFA_UI] refreshIssues error', error);
        }
      });
  }

  private getDuplicateIdsFromIssue(issue: GroupedIssue): number[] {
    const ids: number[] = [];
    const main = this.tarifaDetallesFormArray.at(issue.rowIndex) as FormGroup | undefined;
    const mainId = main?.get('id')?.value;
    if (mainId) ids.push(Number(mainId));
    (issue.relatedRowIndexes ?? []).forEach(idx => {
      const g = this.tarifaDetallesFormArray.at(idx) as FormGroup | undefined;
      const id = g?.get('id')?.value;
      if (id) ids.push(Number(id));
    });
    return Array.from(new Set(ids));
  }

  private formatPrecio(value: any): string | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    return String(value);
  }

  private normalizeEntity(value: any): any {
    if (!value || typeof value === 'string' || typeof value === 'number') {
      return value;
    }
    if (value.displayName) {
      return {
        ...value,
        id: value.id !== undefined && value.id !== null ? String(value.id) : value.id
      };
    }
    const codigo = value.codigo || '';
    const nombre = value.nombre || '';
    const displayName = `${codigo} - ${nombre}`.trim();
    return {
      ...value,
      id: value.id !== undefined && value.id !== null ? String(value.id) : value.id,
      displayName: displayName || value.nombre || value.codigo || `#${value.id ?? ''}`
    };
  }

  private withDisplayName(value: any): any {
    if (!value) return value;
    const codigo = value.codigo || '';
    const nombre = value.nombre || '';
    const displayName = `${codigo} - ${nombre}`.trim();
    return this.normalizeEntity({
      ...value,
      id: value.id !== undefined && value.id !== null ? String(value.id) : value.id,
      displayName: displayName || value.nombre || value.codigo || `#${value.id ?? ''}`
    });
  }

  private parseTimeToDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string') {
      const raw = value.includes('T') ? value : `1970-01-01T${value}`;
      const sanitized = raw.replace(/Z$/, '').split('+')[0];
      const date = new Date(sanitized);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  private formatTime(value: any): string | null {
    if (!value) return null;
    if (value instanceof Date && !isNaN(value.getTime())) {
      const hours = value.getHours().toString().padStart(2, '0');
      const minutes = value.getMinutes().toString().padStart(2, '0');
      const seconds = value.getSeconds().toString().padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    }
    return value;
  }

  private extractId(value: any): string | null {
    if (!value) return null;
    if (Array.isArray(value)) {
      return value.length ? this.extractId(value[0]) : null;
    }
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') {
      const match = value.match(/\/(\d+)$/);
      if (match) return match[1];
      if (/^\d+$/.test(value)) return value;
      return null;
    }
    if (value.id !== undefined && value.id !== null) return String(value.id);
    return null;
  }

  private getTipoProductoById(id: number | string | null, group?: FormGroup): TipoProducto | null {
    if (id === null || id === undefined || id === '') return null;
    const match = this.tipoProductoOptions().find(tipo => String(tipo.id) === String(id));
    if (match) return match;
    if (group) {
      const raw = this.detalleRawTipoProducto.get(group);
      if (raw && String(raw.id) === String(id)) {
        return raw as TipoProducto;
      }
    }
    return null;
  }

  private seedDetalleRelation(value: any, suggestionsSignal: any): void {
    const resolved = Array.isArray(value) ? value[0] : value;
    if (!resolved || typeof resolved !== 'object') {
      return;
    }
    const mapped = this.withDisplayName(resolved);
    this.ensureSuggestionItem(suggestionsSignal, mapped);
  }

  private ensureSuggestionItem(signalRef: any, item: any): void {
    if (!item || !signalRef) return;
    const current = signalRef();
    const id = this.extractId(item);
    if (id === null || id === undefined) return;
    const exists = current.some((entry: any) => String(this.extractId(entry)) === String(id));
    if (!exists) {
      signalRef.set([item, ...current]);
    }
  }

  private getInvalidControls(formGroup: FormGroup, prefix = ''): Record<string, any> {
    const invalid: Record<string, any> = {};
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      const path = prefix ? `${prefix}.${key}` : key;
      if (control instanceof FormGroup) {
        Object.assign(invalid, this.getInvalidControls(control, path));
      } else if (control && control.invalid) {
        invalid[path] = control.errors ?? true;
      }
    });
    return invalid;
  }

}
